<script lang="ts">
  import type { Item } from '@streepaf/shared'
  import { dndzone, type DndEvent } from 'svelte-dnd-action'
  import { listsApi } from '../lib/api/lists.js'
  import { i18n, t } from '../lib/i18n/index.svelte.js'
  import { router } from '../lib/router.svelte.js'
  import { ListStore } from '../lib/state/listStore.svelte.js'
  import { myLists } from '../lib/state/myLists.svelte.js'
  import { prefs } from '../lib/state/prefs.svelte.js'
  import { toasts } from '../lib/state/toasts.svelte.js'
  import { copyText, listAsText } from '../lib/utils/share.js'
  import AddItemRow from '../components/list/AddItemRow.svelte'
  import ItemRow from '../components/list/ItemRow.svelte'
  import ListHeader from '../components/list/ListHeader.svelte'
  import ConnectionChip from '../components/shell/ConnectionChip.svelte'
  import ShareSheet from '../components/share/ShareSheet.svelte'
  import Button from '../components/ui/Button.svelte'
  import Icon from '../components/ui/Icon.svelte'
  import IconButton from '../components/ui/IconButton.svelte'
  import Menu from '../components/ui/Menu.svelte'
  import MenuItem from '../components/ui/MenuItem.svelte'
  import Sheet from '../components/ui/Sheet.svelte'

  interface Props {
    token: string
  }
  const { token }: Props = $props()

  const store = new ListStore()
  let addRow = $state<ReturnType<typeof AddItemRow> | null>(null)

  let menuOpen = $state(false)
  let shareOpen = $state(false)
  let manageOpen = $state(false)
  let infoOpen = $state(false)
  let confirmDelete = $state(false)
  let fieldSheet = $state<{ id: string; field: 'note' | 'quantity' | 'claim' } | null>(null)
  let fieldDraft = $state('')
  let dragging = $state(false)

  const list = $derived(store.list)
  const prefsKey = $derived(token)
  const hideCompleted = $derived(prefs.forList(prefsKey).hideCompleted)
  const completedCollapsed = $derived(prefs.forList(prefsKey).completedCollapsed)

  $effect(() => {
    void store.open(token)
    return () => store.close()
  })

  // The tab title doubles as a progress indicator across open tabs.
  $effect(() => {
    if (!list) return
    const { checked, total } = store.stats
    document.title = total > 0 ? `${checked}/${total} · ${list.title} — Streepaf` : `${list.title} — Streepaf`
    return () => {
      document.title = 'Streepaf'
    }
  })

  $effect(() => {
    if (store.status === 'ready' && router.query.get('new') === '1') router.clearQuery()
  })

  const unchecked = $derived(store.unchecked)
  const completed = $derived(store.completed)

  function onDnd(event: CustomEvent<DndEvent<Item>>, finalize: boolean): void {
    const next = event.detail.items
    dragging = !finalize
    // Reordering only ever moves the visible group; the rest keeps its order.
    const visible = new Set(next.map((item) => item.id))
    const rest = store.items.filter((item) => !visible.has(item.id))
    store.items = [...next, ...rest.filter((item) => item.checked)]
    if (finalize) {
      dragging = false
      store.reorder([...next, ...rest.filter((item) => item.checked)].map((item) => item.id))
    }
  }

  function moveBy(id: string, direction: -1 | 1): void {
    const ordered = store.items
    const index = ordered.findIndex((item) => item.id === id)
    const target = index + direction
    if (index === -1 || target < 0 || target >= ordered.length) return
    const neighbour = ordered[target]
    if (!neighbour) return
    store.move(id, direction === -1 ? { beforeId: neighbour.id } : { afterId: neighbour.id })
  }

  function openField(id: string, field: 'text' | 'note' | 'quantity' | 'claim'): void {
    if (!id) {
      addRow?.focus()
      return
    }
    if (field === 'text') return
    const item = store.items.find((entry) => entry.id === id)
    if (!item) return
    fieldDraft =
      field === 'note' ? item.note : field === 'quantity' ? (item.quantity ?? '') : prefs.displayName
    fieldSheet = { id, field }
  }

  function commitField(): void {
    if (!fieldSheet) return
    const value = fieldDraft.trim()
    const { id, field } = fieldSheet
    fieldSheet = null
    if (field === 'note') store.updateItem(id, { note: value })
    else if (field === 'quantity') store.updateItem(id, { quantity: value || null })
    else {
      if (value) prefs.setName(value)
      store.updateItem(id, { claimedBy: value || null })
    }
  }

  async function copyAsText(): Promise<void> {
    if (!list) return
    if (await copyText(listAsText(list.title, list.emoji, store.items))) {
      toasts.show(t('toast_text_copied'))
    }
  }

  async function duplicate(): Promise<void> {
    if (!list) return
    menuOpen = false
    try {
      const copy = await listsApi.duplicate(token, {})
      myLists.remember({
        token: copy.tokens.edit ?? copy.tokens.view,
        role: 'admin',
        title: copy.title,
        emoji: copy.emoji,
        total: copy.stats.total,
        checked: copy.stats.checked,
      })
      router.navigate(`/e/${copy.tokens.edit}`)
    } catch {
      toasts.error(t('error_title'))
    }
  }

  async function removeList(): Promise<void> {
    confirmDelete = false
    try {
      await listsApi.remove(token)
      myLists.forget(token)
      router.navigate('/')
    } catch {
      toasts.error(t('error_title'))
    }
  }

  async function rotate(kind: 'view' | 'edit'): Promise<void> {
    try {
      await listsApi.rotate(token, [kind])
      toasts.show(t('manage_rotated'))
      await store.open(token)
    } catch {
      toasts.error(t('error_title'))
    }
  }

  function onKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement | null
    const typing = target?.matches('input, textarea, [contenteditable]')
    if (typing) return
    if (event.key === '/') {
      event.preventDefault()
      addRow?.focus()
    }
  }
</script>

<svelte:window onkeydown={onKeydown} />

{#if store.status === 'loading'}
  <div class="page center">
    <div class="skeleton"></div>
    <div class="skeleton"></div>
    <div class="skeleton"></div>
  </div>
{:else if store.status === 'missing'}
  <div class="page center state">
    <h1>{t('notfound_title')}</h1>
    <p>{t('notfound_body')}</p>
    <div class="state-actions">
      <Button variant="primary" onclick={() => router.navigate('/')}>{t('notfound_home')}</Button>
    </div>
  </div>
{:else if store.status === 'failed'}
  <div class="page center state">
    <h1>{t('load_failed')}</h1>
    <p>{t('load_failed_body')}</p>
    <div class="state-actions">
      <Button variant="primary" onclick={() => store.open(token)}>{t('error_retry')}</Button>
    </div>
  </div>
{:else if list}
  <div class="bar no-print">
    <a class="home" href="/" aria-label="Streepaf"><Icon name="back" size={20} /></a>
    <ConnectionChip {store} />
    <div class="bar-actions">
      <IconButton icon="share" label={t('menu_share')} tone="accent" onclick={() => (shareOpen = true)} />
      <div class="menu-anchor">
        <IconButton icon="more" label={t('menu')} onclick={() => (menuOpen = !menuOpen)} />
        <Menu open={menuOpen} onclose={() => (menuOpen = false)}>
          <MenuItem icon="share" onclick={() => { menuOpen = false; shareOpen = true }}>
            {t('menu_share')}
          </MenuItem>
          <MenuItem icon="copy" onclick={() => { menuOpen = false; copyAsText() }}>
            {t('menu_copy_text')}
          </MenuItem>
          {#if store.canEdit}
            <MenuItem icon="plus" onclick={() => { menuOpen = false; store.addItems([{ kind: 'section', text: t('section_placeholder') }]) }}>
              {t('section_add')}
            </MenuItem>
            <MenuItem icon="trash" onclick={() => { menuOpen = false; store.clearCompleted() }}>
              {t('menu_clear_completed')}
            </MenuItem>
            <MenuItem icon="undo" onclick={() => { menuOpen = false; store.uncheckAll() }}>
              {t('menu_uncheck_all')}
            </MenuItem>
            <MenuItem icon="list" onclick={() => { menuOpen = false; store.sortAlphabetically() }}>
              {t('menu_sort_alpha')}
            </MenuItem>
          {/if}
          <MenuItem icon="copy" onclick={duplicate}>
            {store.canEdit ? t('menu_duplicate') : t('menu_copy_to_own')}
          </MenuItem>
          <MenuItem icon="download" onclick={() => { menuOpen = false; window.open(`/api/lists/${token}/export?format=markdown&download=true`, '_blank') }}>
            {t('menu_export_md')}
          </MenuItem>
          <MenuItem icon="list" onclick={() => { menuOpen = false; window.print() }}>
            {t('menu_print')}
          </MenuItem>
          <MenuItem
            icon="eye"
            checked={hideCompleted}
            onclick={() => prefs.updateList(prefsKey, { hideCompleted: !hideCompleted })}
          >
            {t('menu_hide_completed')}
          </MenuItem>
          <MenuItem icon="globe" onclick={() => i18n.toggle()}>
            {t('menu_language')}: {i18n.locale.toUpperCase()}
          </MenuItem>
          <MenuItem
            icon="sun"
            onclick={() => prefs.setTheme(prefs.theme === 'dark' ? 'light' : prefs.theme === 'light' ? 'auto' : 'dark')}
          >
            {t('menu_theme')}: {prefs.theme === 'auto' ? t('menu_theme_auto') : prefs.theme === 'light' ? t('menu_theme_light') : t('menu_theme_dark')}
          </MenuItem>
          <MenuItem icon="info" onclick={() => { menuOpen = false; infoOpen = true }}>
            {t('menu_info')}
          </MenuItem>
          {#if store.capability === 'admin'}
            <MenuItem icon="key" onclick={() => { menuOpen = false; manageOpen = true }}>
              {t('menu_manage')}
            </MenuItem>
            <MenuItem icon="trash" tone="danger" onclick={() => { menuOpen = false; confirmDelete = true }}>
              {t('menu_delete_list')}
            </MenuItem>
          {/if}
        </Menu>
      </div>
    </div>
  </div>

  {#if store.connection === 'offline'}
    <div class="banner offline no-print" role="alert">
      {store.canEdit ? t('connection_offline_edit') : t('connection_offline_view')}
    </div>
  {/if}
  {#if !store.canEdit}
    <div class="banner view no-print">
      <Icon name="eye" size={16} />
      {t('view_only_banner')}
    </div>
  {/if}

  <main class="page">
    <ListHeader {store} autofocusTitle={router.query.get('new') === '1'} />

    <ul
      class="items"
      use:dndzone={{
        items: unchecked,
        dragDisabled: !store.canEdit,
        flipDurationMs: 200,
        dropTargetStyle: {},
        delayTouchStart: 250,
      }}
      onconsider={(e) => onDnd(e, false)}
      onfinalize={(e) => onDnd(e, true)}
    >
      {#each unchecked as item (item.id)}
        <ItemRow {item} {store} onedit={openField} onmove={moveBy} />
      {/each}
    </ul>

    {#if store.canEdit}
      <AddItemRow bind:this={addRow} {store} />
    {/if}

    {#if store.items.length === 0}
      <div class="empty">
        <p class="empty-title">{t('empty_title')}</p>
        <p>{t('empty_body')}</p>
      </div>
    {:else if unchecked.length === 0 && completed.length > 0}
      <p class="all-done">{t('all_done_body')}</p>
    {/if}

    {#if completed.length > 0}
      {#if hideCompleted}
        <button class="completed-toggle" onclick={() => prefs.updateList(prefsKey, { hideCompleted: false })}>
          {t('completed_hidden', { count: completed.length })}
        </button>
      {:else}
        <button
          class="completed-toggle"
          aria-expanded={!completedCollapsed}
          onclick={() => prefs.updateList(prefsKey, { completedCollapsed: !completedCollapsed })}
        >
          <span class="chevron" class:collapsed={completedCollapsed}><Icon name="chevron" size={16} /></span>
          {t('completed_group', { count: completed.length })}
        </button>
        {#if !completedCollapsed}
          <ul class="items completed">
            {#each completed as item (item.id)}
              <ItemRow {item} {store} onedit={openField} onmove={moveBy} />
            {/each}
          </ul>
        {/if}
      {/if}
    {/if}
  </main>

  <ShareSheet open={shareOpen} {store} onclose={() => (shareOpen = false)} />

  <Sheet
    open={fieldSheet !== null}
    title={fieldSheet?.field === 'note'
      ? t('item_note')
      : fieldSheet?.field === 'quantity'
        ? t('item_quantity')
        : t('item_claim')}
    onclose={() => (fieldSheet = null)}
  >
    <input
      class="field"
      bind:value={fieldDraft}
      placeholder={fieldSheet?.field === 'note'
        ? t('item_note_placeholder')
        : fieldSheet?.field === 'quantity'
          ? t('item_quantity_placeholder')
          : t('name_placeholder')}
      onkeydown={(e) => {
        if (e.key === 'Enter') commitField()
      }}
    />
    <div class="sheet-actions">
      <Button variant="primary" onclick={commitField}>{t('save')}</Button>
    </div>
  </Sheet>

  <Sheet open={infoOpen} title={t('menu_info')} onclose={() => (infoOpen = false)}>
    <ul class="facts">
      <li>{t('info_created', { date: i18n.date(list.createdAt) })}</li>
      <li>{t('info_expires', { date: i18n.date(list.expiresAt) })}</li>
      <li>{t('info_items', { count: store.stats.total })}</li>
    </ul>
    <p class="tip"><a href="/llms.txt" target="_blank" rel="noopener" data-native>{t('home_devs')}</a></p>
  </Sheet>

  <Sheet open={manageOpen} title={t('manage_title')} onclose={() => (manageOpen = false)}>
    <p class="warn">{t('manage_admin_warning')}</p>
    <input class="field" readonly value={list.links.admin ?? ''} onfocus={(e) => e.currentTarget.select()} />
    <p class="tip">{t('manage_rotate_warning')}</p>
    <div class="sheet-actions">
      <Button onclick={() => rotate('edit')}>{t('manage_rotate_edit')}</Button>
      <Button onclick={() => rotate('view')}>{t('manage_rotate_view')}</Button>
    </div>
  </Sheet>

  <Sheet open={confirmDelete} title={t('confirm_delete_list_title')} onclose={() => (confirmDelete = false)}>
    <p>{t('confirm_delete_list_body')}</p>
    <div class="sheet-actions">
      <Button variant="ghost" onclick={() => (confirmDelete = false)}>{t('confirm_cancel')}</Button>
      <Button variant="danger" onclick={removeList}>{t('confirm_delete_list_yes')}</Button>
    </div>
  </Sheet>
{/if}

<style>
  .bar {
    position: sticky;
    top: 0;
    z-index: 30;
    display: flex;
    align-items: center;
    gap: var(--space-2);
    max-width: var(--max-width);
    margin: 0 auto;
    padding: var(--space-1) var(--space-2);
    background: color-mix(in srgb, var(--bg) 88%, transparent);
    backdrop-filter: blur(8px);
  }
  .home {
    display: grid;
    place-items: center;
    width: 44px;
    height: 44px;
    border-radius: var(--radius);
    color: var(--fg-muted);
  }
  .home:hover {
    background: var(--surface-2);
  }
  .bar-actions {
    display: flex;
    align-items: center;
    gap: var(--space-1);
    margin-left: auto;
  }
  .menu-anchor {
    position: relative;
  }
  .banner {
    max-width: var(--max-width);
    margin: 0 auto var(--space-2);
    padding: var(--space-2) var(--space-4);
    border-radius: var(--radius);
    font-size: 14px;
  }
  .banner.offline {
    background: var(--danger-soft);
    color: var(--danger);
  }
  .banner.view {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    background: var(--surface-2);
    color: var(--fg-muted);
  }
  .items {
    list-style: none;
    margin: 0;
    padding: 0;
    border-radius: var(--radius);
    background: var(--surface);
    box-shadow: var(--shadow-1);
  }
  .items.completed {
    margin-top: var(--space-2);
    opacity: 0.85;
  }
  .empty {
    padding: var(--space-5) var(--space-2);
    text-align: center;
    color: var(--fg-muted);
  }
  .empty-title {
    font-weight: 600;
    color: var(--fg);
  }
  .all-done {
    padding: var(--space-3) var(--space-2);
    color: var(--fg-muted);
    font-size: 15px;
  }
  .completed-toggle {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    width: 100%;
    min-height: 44px;
    margin-top: var(--space-4);
    padding: 0 var(--space-2);
    border: none;
    background: transparent;
    font-size: 14px;
    font-weight: 600;
    color: var(--fg-muted);
  }
  .chevron {
    display: inline-flex;
    transition: transform var(--dur-fast) var(--ease);
  }
  .chevron.collapsed {
    transform: rotate(-90deg);
  }
  .center {
    padding-top: var(--space-6);
  }
  .skeleton {
    height: 52px;
    margin-bottom: var(--space-2);
    border-radius: var(--radius);
    background: var(--surface-2);
    animation: shimmer 1.4s infinite;
  }
  @keyframes shimmer {
    50% {
      opacity: 0.55;
    }
  }
  .state {
    text-align: center;
  }
  .state h1 {
    font-size: 22px;
    margin-bottom: var(--space-2);
  }
  .state p {
    color: var(--fg-muted);
  }
  .state-actions {
    display: flex;
    justify-content: center;
    gap: var(--space-2);
    margin-top: var(--space-4);
  }
  .field {
    width: 100%;
    min-height: 44px;
    padding: 0 var(--space-3);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    background: var(--surface-2);
    font-size: 15px;
  }
  .sheet-actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: var(--space-2);
    margin-top: var(--space-4);
  }
  .facts {
    margin: 0;
    padding-left: var(--space-4);
    color: var(--fg-muted);
  }
  .warn {
    margin-bottom: var(--space-3);
    color: var(--warn);
    font-size: 14px;
  }
  .tip {
    margin-top: var(--space-3);
    font-size: 13px;
    color: var(--fg-faint);
  }
</style>
